import { ReactNode, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import * as api from 'api/api'
import { Attachment, Section } from 'api/types'
import { useRegion } from 'components/app-shell/context/RegionContext'
import { Button } from 'components/ui'
import { formatNumber } from 'lib/format'
import { printLocale } from 'lib/region'
import { useAsync } from 'lib/hooks'

import { Thumb } from './Attachments'
import { PP_LABELS, PRINT_WORKFLOW, PrintLocale, printDate, printDateTime, printWeekday } from './print-locale'

const Cell = ({
  kind = 'body',
  align,
  width,
  children,
}: {
  kind?: 'header' | 'body' | 'total'
  align?: 'end'
  width?: number
  children?: ReactNode
}) => (
  <div
    className={['pp-cell', `pp-cell--${kind}`, align ? 'pp-cell--end' : ''].filter(Boolean).join(' ')}
    style={width ? { flex: `0 0 ${width}px` } : undefined}
  >
    {children}
  </div>
)

const SectionTitle = ({ index, title, meta }: { index: number; title: string; meta?: string }) => (
  <div className="pp-section-title">
    <p>
      {index}. {title}
    </p>
    {meta ? <span>{meta}</span> : null}
  </div>
)

const attachmentsFor = (list: Attachment[], section: Section, lineId?: string) =>
  list.filter((item) => item.section === section && (lineId === undefined || item.lineId === lineId))

const LangToggle = ({ locale, onChange }: { locale: PrintLocale; onChange: (locale: PrintLocale) => void }) => (
  <div className="pp-lang" role="group" aria-label={locale === 'ar' ? 'اللغة' : 'Language'}>
    <button
      type="button"
      className={locale === 'en' ? 'pp-lang__btn is-active' : 'pp-lang__btn'}
      aria-label="Switch to English"
      onClick={() => onChange('en')}
    >
      EN
    </button>
    <button
      type="button"
      className={locale === 'ar' ? 'pp-lang__btn is-active' : 'pp-lang__btn'}
      lang="ar"
      aria-label={locale === 'en' ? 'Change language to Arabic' : 'التبديل إلى الإنجليزية'}
      onClick={() => onChange('ar')}
    >
      ع
    </button>
  </div>
)

export const PrintPreviewPage = () => {
  const { reportId = '' } = useParams()
  const { settings } = useRegion()
  const [locale, setLocale] = useState<PrintLocale>(printLocale)
  const [overridden, setOverridden] = useState(false)
  const t = PP_LABELS[locale]
  const dir = locale === 'ar' ? 'rtl' : 'ltr'
  const { data: report, error, loading } = useAsync(() => api.fetchReport(reportId), [reportId])

  useEffect(() => {
    if (!overridden && settings) setLocale(printLocale())
  }, [overridden, settings])

  if (loading && !report) return <div className="pp-shell pp-shell--center">{t.loading}</div>
  if (error || !report)
    return (
      <div className="pp-shell pp-shell--center">
        {t.loadError}
        {error ? `: ${error}` : ''}
      </div>
    )

  const staffTotal = report.staff.reduce((sum, line) => sum + (line.count || 0), 0)
  const equipmentTotal = report.equipment.reduce((sum, line) => sum + (line.count || 0), 0)
  const workPhotos = report.attachments.filter((item) => item.section === 'WORKS')
  const supervisionFiles = attachmentsFor(report.attachments, 'SUPERVISION')
  const generatedAt = printDate(locale, new Date().toISOString())

  return (
    <div className="pp-shell" dir={dir} lang={locale}>
      <div className="pp-toolbar">
        <LangToggle
          locale={locale}
          onChange={(next) => {
            setOverridden(true)
            setLocale(next)
          }}
        />
        <Button onClick={() => window.close()}>{t.close}</Button>
        <Button variant="primary" icon="download" onClick={() => window.print()}>
          {t.print}
        </Button>
      </div>

      <div className="pp-page">
        <div className="pp-header">
          <div className="pp-brand">
            <div className="pp-logo">JoPath</div>
            <div>
              <p className="pp-project">{report.project.name}</p>
              {report.project.tenderNumber ? (
                <p className="pp-muted">
                  {t.infoTender} {report.project.tenderNumber}
                </p>
              ) : null}
            </div>
          </div>
          <div className="pp-report-meta">
            <p className="pp-h3">{t.reportTitle}</p>
            <p className="pp-mono">{report.code}</p>
            <p>
              {printWeekday(locale, report.reportDate, report.dayName)} · {printDate(locale, report.reportDate)}
            </p>
          </div>
        </div>

        <div className="pp-info">
          <div className="pp-row">
            <Cell kind="header" width={150}>
              {t.infoProject}
            </Cell>
            <Cell>{report.project.name}</Cell>
            <Cell kind="header" width={150}>
              {t.infoTender}
            </Cell>
            <Cell>{report.project.tenderNumber || t.dash}</Cell>
          </div>
          <div className="pp-row">
            <Cell kind="header" width={150}>
              {t.infoConsultant}
            </Cell>
            <Cell>{report.project.consultant || t.dash}</Cell>
            <Cell kind="header" width={150}>
              {t.infoDay}
            </Cell>
            <Cell>{printWeekday(locale, report.reportDate, report.dayName)}</Cell>
          </div>
          <div className="pp-row">
            <Cell kind="header" width={150}>
              {t.infoWeather}
            </Cell>
            <Cell>{report.weather || t.dash}</Cell>
            <Cell kind="header" width={150}>
              {t.infoPreparedBy}
            </Cell>
            <Cell>{report.preparedByName || t.dash}</Cell>
          </div>
        </div>

        <div className="pp-section">
          <SectionTitle index={1} title={t.staffTitle} meta={`${t.staffOnSite(formatNumber(staffTotal))} · ${t.staffJobTitles(report.staff.length)}`} />
          <div className="pp-table">
            <div className="pp-row pp-row--head">
              <Cell kind="header">{t.colJobTitle}</Cell>
              <Cell kind="header" align="end" width={80}>
                {t.colCount}
              </Cell>
              <Cell kind="header" width={300}>
                {t.colNameNote}
              </Cell>
            </div>
            {report.staff.map((line, index) => (
              <div className="pp-row" key={line.id || index}>
                <Cell>{line.title}</Cell>
                <Cell align="end" width={80}>
                  {formatNumber(line.count)}
                </Cell>
                <Cell width={300}>{line.name || t.dash}</Cell>
              </div>
            ))}
            <div className="pp-row pp-row--total">
              <Cell kind="total">{t.total}</Cell>
              <Cell kind="total" align="end" width={80}>
                {formatNumber(staffTotal)}
              </Cell>
              <Cell kind="total" width={300} />
            </div>
          </div>
        </div>

        {report.labTests.length ? (
          <div className="pp-section">
            <SectionTitle index={2} title={t.labTitle} meta={t.labTests(report.labTests.length)} />
            <div className="pp-table">
              <div className="pp-row pp-row--head">
                <Cell kind="header">{t.colTestType}</Cell>
                <Cell kind="header" width={180}>
                  {t.colLocation}
                </Cell>
                <Cell kind="header" width={130}>
                  {t.colResult}
                </Cell>
                <Cell kind="header" width={150}>
                  {t.colLabResult}
                </Cell>
              </div>
              {report.labTests.map((line, index) => {
                const file = attachmentsFor(report.attachments, 'LAB_TESTS', line.id)[0]
                return (
                  <div className="pp-row" key={line.id || index}>
                    <Cell>{line.testType}</Cell>
                    <Cell width={180}>{line.location || t.dash}</Cell>
                    <Cell width={130}>{line.result || t.dash}</Cell>
                    <Cell width={150}>{file?.originalName || t.dash}</Cell>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {report.materials.length ? (
          <div className="pp-section">
            <SectionTitle index={3} title={t.materialsTitle} meta={t.materialsDeliveries(report.materials.length)} />
            <div className="pp-table">
              <div className="pp-row pp-row--head">
                <Cell kind="header">{t.colMaterial}</Cell>
                <Cell kind="header" align="end" width={120}>
                  {t.colQuantity}
                </Cell>
                <Cell kind="header" width={200}>
                  {t.colDeliveryNote}
                </Cell>
              </div>
              {report.materials.map((line, index) => {
                const file = attachmentsFor(report.attachments, 'MATERIALS', line.id)[0]
                return (
                  <div className="pp-row" key={line.id || index}>
                    <Cell>{line.name}</Cell>
                    <Cell align="end" width={120}>
                      {line.quantity || t.dash}
                    </Cell>
                    <Cell width={200}>{file?.originalName || t.dash}</Cell>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}

        {report.equipment.length ? (
          <div className="pp-section">
            <SectionTitle index={4} title={t.equipmentTitle} meta={t.equipmentUnits(formatNumber(equipmentTotal))} />
            <div className="pp-table">
              <div className="pp-row pp-row--head">
                <Cell kind="header">{t.colMachine}</Cell>
                <Cell kind="header" align="end" width={80}>
                  {t.colCount}
                </Cell>
              </div>
              {report.equipment.map((line, index) => (
                <div className="pp-row" key={line.id || index}>
                  <Cell>{line.name}</Cell>
                  <Cell align="end" width={80}>
                    {formatNumber(line.count)}
                  </Cell>
                </div>
              ))}
              <div className="pp-row pp-row--total">
                <Cell kind="total">{t.total}</Cell>
                <Cell kind="total" align="end" width={80}>
                  {formatNumber(equipmentTotal)}
                </Cell>
              </div>
            </div>
          </div>
        ) : null}

        {report.works.length ? (
          <div className="pp-section pp-break-before">
            <SectionTitle index={5} title={t.worksTitle} meta={t.worksItems(report.works.length, workPhotos.length)} />
            <div className="pp-table">
              <div className="pp-row pp-row--head">
                <Cell kind="header" width={40}>
                  {t.colIndex}
                </Cell>
                <Cell kind="header">{t.colWorkDescription}</Cell>
              </div>
              {report.works.map((line, index) => (
                <div className="pp-row" key={line.id || index}>
                  <Cell width={40}>{index + 1}</Cell>
                  <Cell>{line.text}</Cell>
                </div>
              ))}
            </div>
            {workPhotos.length ? (
              <div className="pp-photos">
                {workPhotos.map((photo, index) => (
                  <div className="pp-photo" key={photo.id}>
                    <div className="pp-photo__frame">
                      <Thumb attachment={photo} />
                    </div>
                    <p>
                      {index + 1}. {photo.caption || photo.originalName} · {printDateTime(locale, photo.createdAt)}
                    </p>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}

        {report.visits.length ? (
          <div className="pp-section">
            <SectionTitle index={6} title={t.visitsTitle} meta={t.visitsCount(report.visits.length)} />
            <div className="pp-table">
              <div className="pp-row pp-row--head">
                <Cell kind="header" width={260}>
                  {t.colVisitingParty}
                </Cell>
                <Cell kind="header">{t.colReason}</Cell>
              </div>
              {report.visits.map((line, index) => (
                <div className="pp-row" key={line.id || index}>
                  <Cell width={260}>{line.party}</Cell>
                  <Cell>
                    {line.reason || (line.minutes ? '' : t.dash)}
                    {line.minutes ? (
                      <p>
                        {t.colMinutes}: {line.minutes}
                      </p>
                    ) : null}
                  </Cell>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {report.notes.length || supervisionFiles.length ? (
          <div className="pp-section">
            <SectionTitle index={7} title={t.supervisionTitle} meta={t.supervisionMeta(report.notes.length, supervisionFiles.length)} />
            <div className="pp-notes">
              {report.notes.map((note) => (
                <p key={note.id}>
                  {note.authorName} · {printDateTime(locale, note.createdAt)} — {note.text}
                </p>
              ))}
              {supervisionFiles.map((file) => (
                <p key={file.id}>
                  {t.fileLabel}: {file.originalName}
                </p>
              ))}
            </div>
          </div>
        ) : null}

        {report.submittedByName || report.approvedByName ? (
          <div className="pp-section">
            <SectionTitle index={8} title={t.workflowTitle} meta={PRINT_WORKFLOW[locale][report.workflow]} />
            <div className="pp-signatures">
              {report.submittedByName ? (
                <div className="pp-signature">
                  <p className="pp-muted">{t.submittedBy}</p>
                  <p className="pp-signature__name">{report.submittedByName}</p>
                  <p>{printDateTime(locale, report.submittedAt)}</p>
                </div>
              ) : null}
              {report.approvedByName ? (
                <div className="pp-signature">
                  <p className="pp-muted">{t.approvedBy}</p>
                  <p className="pp-signature__name">{report.approvedByName}</p>
                  <p>{printDateTime(locale, report.approvedAt)}</p>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        <div className="pp-footer">
          <span>
            {report.code} · {t.footerGenerated(generatedAt)}
          </span>
        </div>
      </div>
    </div>
  )
}
